import React, { useState, useEffect } from "react";
import { 
  Users, 
  Search, 
  Plus, 
  Mail, 
  Phone, 
  Calendar, 
  Activity, 
  Heart, 
  Droplets, 
  Thermometer, 
  X,
  User,
  FileText,
  ChevronDown
} from "lucide-react";
import DoctorSidebar from "./DoctorSidebar";
import "./doctor.css";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { db } from "../../firebase/config";
import { ref, onValue, push, set } from "firebase/database";

const Patients = () => {
  const { currentUser } = useAuth();
  const { showAlert } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedMonitor, setSelectedMonitor] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);

  const [newPatient, setNewPatient] = useState({
    name: "",
    age: "",
    condition: "",
    email: "",
    phone: ""
  });

  useEffect(() => {
    if (!currentUser) return;

    // 1. Synchronize Clinical Identity Registry
    const usersRef = ref(db, "users");
    const vitalsRef = ref(db, "vitals");

    let patientsList = [];
    let allVitals = {};

    const unsubscribeUsers = onValue(usersRef, (snapshot) => {
      const usersData = snapshot.val();
      if (usersData) {
        patientsList = Object.entries(usersData)
          .filter(([uid, data]) => data && data.role === "patient")
          .map(([uid, data]) => ({
            uid: uid,
            id: uid.substring(0, 8).toUpperCase(),
            name: data.name || "Unknown Patient",
            initials: (data.name || "U").split(" ").map(n => n[0]).join("").toUpperCase(),
            age: data.age || 45,
            condition: data.condition || "General Checkup",
            email: data.email || "N/A",
            phone: data.phone || "N/A",
            lastVisit: data.lastVisit || "2024-01-20",
            status: "active",
            vitals: { hr: 0, spo2: 0, temp: 0 }
          }));
      } else {
        patientsList = [];
      }
      updatePatientsWithVitals();
    });

    const unsubscribeVitals = onValue(vitalsRef, (snapshot) => {
      allVitals = snapshot.val() || {};
      updatePatientsWithVitals();
    });

    const updatePatientsWithVitals = () => {
      const updated = patientsList.map(p => {
        const v = allVitals[p.uid];
        if (v) {
          const isCritical = v.heartRate > 105 || v.spo2 < 93;
          return {
            ...p,
            status: isCritical ? "critical" : "active",
            vitals: { 
              hr: v.heartRate, 
              spo2: v.spo2, 
              temp: v.temperature?.toFixed(1) || 0 
            }
          };
        }
        return p;
      });
      setPatients(updated);
      setLoading(false);
    };

    return () => {
      unsubscribeUsers();
      unsubscribeVitals();
    };
  }, [currentUser]);

  const handleAddPatient = async () => {
    if (!newPatient.name || !newPatient.age || !newPatient.condition) {
      showAlert("PROTOCOL_ERROR: Required clinical metadata missing.", "error");
      return;
    }

    try {
      const usersRef = ref(db, "users");
      const newPatientRef = push(usersRef);
      
      const patientData = {
        name: newPatient.name,
        age: parseInt(newPatient.age),
        condition: newPatient.condition,
        email: newPatient.email,
        phone: newPatient.phone,
        role: "patient",
        primaryDoctorId: currentUser.uid,
        status: "active",
        createdAt: new Date().toISOString(),
        lastVisit: new Date().toISOString().split('T')[0]
      };

      await set(newPatientRef, patientData);
      showAlert("IDENTITY_PROVISIONED: New patient node added to registry.", "success");
      setIsAddModalOpen(false);
      setNewPatient({ name: "", age: "", condition: "", email: "", phone: "" });
    } catch (error) {
      console.error("CLINICAL_REGISTRY_ERROR:", error);
      showAlert("REGISTRY_ERROR: " + error.message, "error");
    }
  };

  const filteredPatients = patients.filter(p => 
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.condition.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="dashboard-shell doctor-portal">
      <DoctorSidebar />

      <main className="dashboard-content">
        <header className="patients-header-main">
          <div className="header-text-main">
            <div className="title-row">
              <Users size={28} className="icon-blue" />
              <h1>Patient Directory</h1>
            </div>
            <p>Manage and view all registered patients</p>
          </div>
          <button className="premium-btn primary add-btn" onClick={() => setIsAddModalOpen(true)}>
            <Plus size={18} />
            Add Patient
          </button>
        </header>

        <div className="scrollable-content patients-grid-container">
          <div className="directory-controls">
            <div className="search-bar-large glass-card">
              <Search size={20} className="icon-dim" />
              <input 
                type="text" 
                placeholder="Search by name, ID, or condition..." 
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>

          {loading ? (
            <div className="loading-hud-sector">
              <p>&gt; SYNCHRONIZING_CLINICAL_MATRIX...</p>
            </div>
          ) : (
            <div className="patients-grid">
              {filteredPatients.map((patient) => (
                <div key={patient.uid} className={`patient-card glass-card ${patient.status}`}>
                  <div className="card-top">
                    <div className="patient-info">
                      <div className={`avatar initials ${patient.status}`}>{patient.initials}</div>
                      <div className="meta">
                        <h3>{patient.name}</h3>
                        <span>{patient.id} • {patient.age}y</span>
                      </div>
                    </div>
                    <span className={`status-badge-inline ${patient.status}`}>
                      {patient.status}
                    </span>
                  </div>

                  <div className="card-body">
                    <div className="condition-box">
                      <span className="label">Condition</span>
                      <span className="value">{patient.condition}</span>
                    </div>
                    <div className="contact-list">
                      <div className="contact-item">
                        <Mail size={14} className="icon-dim" />
                        <span>{patient.email}</span>
                      </div>
                      <div className="contact-item">
                        <Phone size={14} className="icon-dim" />
                        <span>{patient.phone}</span>
                      </div>
                      <div className="contact-item">
                        <Calendar size={14} className="icon-dim" />
                        <span>Last visit: {patient.lastVisit}</span>
                      </div>
                    </div>
                  </div>

                  <div className="card-footer-actions">
                    <button className="action-btn-outline" onClick={() => setSelectedProfile(patient)}>
                      View Profile
                    </button>
                    <button className="action-btn-primary" onClick={() => setSelectedMonitor(patient)}>
                      Monitor
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Modal: Patient Profile */}
        {selectedProfile && (
          <div className="modal-backdrop">
            <div className="profile-modal-mini glass-card">
              <div className="modal-header-mini">
                <h2>Patient Profile</h2>
                <button className="close-mini-btn" onClick={() => setSelectedProfile(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-content-mini">
                <p className="id-sub">{selectedProfile.id}</p>
                <div className="header-primary">
                  <div className="avatar initials large">{selectedProfile.initials}</div>
                  <div className="text-box">
                    <h3>{selectedProfile.name}</h3>
                    <p>{selectedProfile.age} years • {selectedProfile.condition}</p>
                    <span className={`status-badge-inline ${selectedProfile.status}`}>{selectedProfile.status}</span>
                  </div>
                </div>
                
                <div className="profile-details-grid">
                  <div className="detail-card glass-card">
                    <Mail size={16} className="icon-blue" />
                    <div className="text">
                      <span className="label">Email</span>
                      <span className="value">{selectedProfile.email}</span>
                    </div>
                  </div>
                  <div className="detail-card glass-card">
                    <Phone size={16} className="icon-blue" />
                    <div className="text">
                      <span className="label">Phone</span>
                      <span className="value">{selectedProfile.phone}</span>
                    </div>
                  </div>
                  <div className="detail-card glass-card">
                    <Calendar size={16} className="icon-blue" />
                    <div className="text">
                      <span className="label">Last Visit</span>
                      <span className="value">{selectedProfile.lastVisit}</span>
                    </div>
                  </div>
                  <div className="detail-card glass-card">
                    <Activity size={16} className="icon-blue" />
                    <div className="text">
                      <span className="label">Condition</span>
                      <span className="value">{selectedProfile.condition}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Live Monitor */}
        {selectedMonitor && (
          <div className="modal-backdrop">
            <div className="monitor-modal-mini glass-card">
              <div className="modal-header-mini">
                <h2>Live Monitor — {selectedMonitor.name}</h2>
                <button className="close-mini-btn" onClick={() => setSelectedMonitor(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-content-mini">
                <p className="id-sub">Real-time vitals overview</p>
                <div className="monitor-grid">
                  <div className="monitor-card glass-card">
                    <Heart size={24} className="icon-red" />
                    <div className="monitor-value">{selectedMonitor.vitals.hr}</div>
                    <span className="label">Heart Rate (bpm)</span>
                  </div>
                  <div className="monitor-card glass-card">
                    <Droplets size={24} className="icon-blue" />
                    <div className="monitor-value">{selectedMonitor.vitals.spo2}%</div>
                    <span className="label">SpO2</span>
                  </div>
                  <div className="monitor-card glass-card">
                    <Thermometer size={24} className="icon-orange" />
                    <div className="monitor-value">{selectedMonitor.vitals.temp}°C</div>
                    <span className="label">Temperature</span>
                  </div>
                  <div className="monitor-card glass-card">
                    <Activity size={24} className="icon-green" />
                    <span className={`status-badge-inline ${selectedMonitor.status}`}>{selectedMonitor.status}</span>
                    <span className="label">Status</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal: Add Patient */}
        {isAddModalOpen && (
          <div className="modal-backdrop">
            <div className="add-patient-modal glass-card">
              <div className="modal-header-mini">
                <div className="title-box">
                  <h2>Add New Patient</h2>
                  <p>Fill in the patient details below.</p>
                </div>
                <button className="close-mini-btn" onClick={() => setIsAddModalOpen(false)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body-form">
                <div className="form-group">
                  <label>Name *</label>
                  <input 
                    type="text" 
                    placeholder="Full name" 
                    value={newPatient.name}
                    onChange={(e) => setNewPatient({...newPatient, name: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Age *</label>
                  <input 
                    type="text" 
                    placeholder="Age" 
                    value={newPatient.age}
                    onChange={(e) => setNewPatient({...newPatient, age: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Condition *</label>
                  <input 
                    type="text" 
                    placeholder="Medical condition" 
                    value={newPatient.condition}
                    onChange={(e) => setNewPatient({...newPatient, condition: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input 
                    type="email" 
                    placeholder="Email" 
                    value={newPatient.email}
                    onChange={(e) => setNewPatient({...newPatient, email: e.target.value})}
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input 
                    type="text" 
                    placeholder="Phone" 
                    value={newPatient.phone}
                    onChange={(e) => setNewPatient({...newPatient, phone: e.target.value})}
                  />
                </div>
              </div>
              <div className="modal-footer-btns">
                <button className="cancel-btn" onClick={() => setIsAddModalOpen(false)}>Cancel</button>
                <button className="premium-btn primary" onClick={handleAddPatient}>Authorize Identity</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Patients;
